import { Dropdown, IDropdownOption, mergeStyles, Separator, Link } from '@fluentui/react';
import * as React from 'react';
import { capturePageAction } from '../../../../web/utils/oneDsUtil';
import { AutomationIds } from '../../../models/constants/automationIds';
import { TimeZoneInfo, SupportedLanguage } from '../models/modalityModels';
import { getContactUsClassNames, getContactUsStyles } from '../styles/contactUsStyles';
import { IContactUsTheme } from '../styles/theme.model';
import { getString } from '../../../../web/utils/stringLoader';

interface IRegionalSettingsProps {
    theme: IContactUsTheme;
    dispatchFetchAvailableModalitiesV2(): void;
    dispatchUpdatePreferredTimeZone(preferredTimeZone: string): void;
    dispatchUpdatePreferredLocale(preferredLocale: string): void;
    isRegionalSettingsEnabled: boolean;
    timeZones: [];
    supportedLanguages: [];
    preferredLocale: string;
    preferredTimeZone: string;
}

export class RegionalSettings extends React.Component<IRegionalSettingsProps, { isRegionalSettingsShown: boolean }> {
    private regionalSettingsStrings = {
      regionalSettingsLabel: getString('regionalSettings_regionalSettingsLabel'),
      timeZoneLabel: getString('regionalSettings_timeZoneLabel'),
      timeZoneDefaultText: getString('regionalSettings_timeZoneDefaultText'),
      supportedLanguageLabel: getString('regionalSettings_supportedLanguageLabel'),
      supportedLanguageDefaultText: getString('regionalSettings_supportedLanguageDefaultText')
    }

    public constructor(props: IRegionalSettingsProps) {
        super(props);
        this.state = {
            isRegionalSettingsShown: (this.props.preferredLocale || this.props.preferredTimeZone) ? true : false,
        };
    }

    public render() {
      const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: this.props.theme });

      return this.props.isRegionalSettingsEnabled && this.props.timeZones && this.props.supportedLanguages
        ? <div data-automation-id={AutomationIds.RegionalSettings}
          className={mergeStyles('col-lg-12')}
        >
          <Separator />
          <div
            className={mergeStyles(contactUsClassNames.fieldSection)}
          >
            <Link
              aria-label={this.regionalSettingsStrings.regionalSettingsLabel}
              aria-expanded={this.state.isRegionalSettingsShown}
              className={mergeStyles(contactUsClassNames.fieldLabel, contactUsClassNames.regionalSettingsTitle, contactUsClassNames.wrapText)}
              onClick={this._onRegionalSettingsClick}
            >
              {this.regionalSettingsStrings.regionalSettingsLabel}
            </Link>
          </div>
          <div className={mergeStyles(contactUsClassNames.fieldSection,
            this.state.isRegionalSettingsShown ? '' : contactUsClassNames.hideRegionalSettings)}>
            <div className={contactUsClassNames.fieldLabel}>
              {this.regionalSettingsStrings.timeZoneLabel}
              <div className={contactUsClassNames.fieldInputGroup}>
                <Dropdown
                  aria-label={this.regionalSettingsStrings.timeZoneDefaultText}
                  options={this.generateTimeZoneOptions()}
                  selectedKey={this.props.preferredTimeZone}
                  onChange={this._onTimeZoneDropdownChange}
                  styles={{ dropdownItem: contactUsClassNames.timeZoneDropdownItem }}
                />
              </div>
            </div>
          </div>
          <div className={mergeStyles(contactUsClassNames.fieldSection,
            this.state.isRegionalSettingsShown ? '' : contactUsClassNames.hideRegionalSettings)}>
            <div className={contactUsClassNames.fieldLabel}>
              {this.regionalSettingsStrings.supportedLanguageLabel}
              <div className={contactUsClassNames.fieldInputGroup}>
                <Dropdown
                  aria-label={this.regionalSettingsStrings.supportedLanguageDefaultText}
                  options={this.generateSupportedLanguageOptions()}
                  selectedKey={this.props.preferredLocale}
                  onChange={this._onSupportedLanguageDropdownChange}
                />
              </div>
            </div>
          </div>
        </div> : null;
  }

    private _onRegionalSettingsClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!!event && !!event.target) {
            event.stopPropagation();
        }
        const logHandlerTags = {
            actionType: 'CL',
            behavior: this.state.isRegionalSettingsShown ? 'HIDE' : 'SHOW',
            content: {
                contentId: 'regionalSettingsExpandCollapse',
                contentName: 'regionalSettingsExpandCollapse',
                areaName: 'RegionalSettings',
                fileName: 'RegionalSettings.tsx',
                methodName: '_onRegionalSettingsClick',
                formnm: 'maxFunnel',
                openRegionalSettings: !this.state.isRegionalSettingsShown,
            },
        };
        capturePageAction(null, logHandlerTags);
        this.setState({ isRegionalSettingsShown: !this.state.isRegionalSettingsShown });
    };

    private _onTimeZoneDropdownChange = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        const logHandlerTags = {
            actionType: 'CL',
            behavior: 'CLICK',
            content: {
                contentId: 'regionalSettingsTimeZoneSelection',
                contentName: 'regionalSettingsTimeZoneSelection',
                areaName: 'RegionalSettings',
                fileName: 'RegionalSettings.tsx',
                methodName: '_onTimeZoneDropdownChange',
                formnm: 'maxFunnel',
                preferredTimeZone: item.key,
            },
        };
        capturePageAction(null, logHandlerTags);
        this.props.dispatchUpdatePreferredTimeZone(String(item.key));
    };

    private _onSupportedLanguageDropdownChange = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
        this.props.dispatchUpdatePreferredLocale(String(item.key));
        this.props.dispatchFetchAvailableModalitiesV2();
        const logHandlerTags = {
            actionType: 'CL',
            behavior: 'CLICK',
            content: {
                contentId: 'regionalSettingsSupportedLanguageSelection',
                contentName: 'regionalSettingsSupportedLanguageSelection',
                areaName: 'RegionalSettings',
                fileName: 'RegionalSettings.tsx',
                methodName: '_onSupportedLanguageDropdownChange',
                formnm: 'maxFunnel',
                preferredLocale: item.key,
            },
        };
        capturePageAction(null, logHandlerTags);
    };

    private generateTimeZoneOptions = (): Array<IDropdownOption> => {
        const timeZoneOptions = this.props.timeZones && this.props.timeZones
        .map((timeZone: TimeZoneInfo) => {
            return {
            key: timeZone.Id,
            text: timeZone.DisplayName,
            };
        }) as Array<IDropdownOption>;

        const timeZoneDefaultOption: IDropdownOption = {
        key: '',
        text: this.regionalSettingsStrings.timeZoneDefaultText,
        };
        timeZoneOptions.unshift(timeZoneDefaultOption);

        return timeZoneOptions;
    };

    private generateSupportedLanguageOptions = (): Array<IDropdownOption> => {
        const supportedLanguageOptions = this.props.supportedLanguages && this.props.supportedLanguages
        .map((supportedLanguage: SupportedLanguage) => {
            return {
            key: supportedLanguage.Name,
            // id: supportedLanguage.LCID,
            text: supportedLanguage.NativeName,
            };
        }) as Array<IDropdownOption>;

        const supportedLanguageDefaultOption: IDropdownOption = {
        key: '',
        text: this.regionalSettingsStrings.supportedLanguageDefaultText,
        };
        supportedLanguageOptions.unshift(supportedLanguageDefaultOption);

        return supportedLanguageOptions;
    };
}
